import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";

import { fetchAuthStatus, login } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Phase = "checking" | "open" | "locked";

/**
 * Wraps the whole app behind the demo access gate. On mount it asks the
 * backend whether this browser is already past the gate (the 7-day cookie);
 * if so — or if auth is disabled — it renders the app immediately. Otherwise
 * it shows a styled login screen. A backend 401 mid-session (cookie expired)
 * re-locks the gate via the ``auth:unauthorized`` window event.
 */
export function LoginGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>("checking");

  useEffect(() => {
    let alive = true;
    fetchAuthStatus()
      .then((status) => {
        if (!alive) return;
        setPhase(!status.enabled || status.authenticated ? "open" : "locked");
      })
      // If the status probe itself fails (backend down, etc.), fall back to
      // the login screen rather than wedging on the spinner.
      .catch(() => alive && setPhase("locked"));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const relock = () => setPhase("locked");
    window.addEventListener("auth:unauthorized", relock);
    return () => window.removeEventListener("auth:unauthorized", relock);
  }, []);

  if (phase === "checking") {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (phase === "locked") {
    return <LoginScreen onSuccess={() => setPhase("open")} />;
  }

  return <>{children}</>;
}

function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Guard against a double-submit racing two login requests.
  const inFlight = useRef(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const status = await login(password);
      if (status.authenticated) {
        onSuccess();
      } else {
        setError("Invalid password.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed.");
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const inputClass = cn(
    "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm",
    "outline-none transition-colors focus:border-slate-400 focus:ring-2 focus:ring-slate-200",
  );

  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-50 px-4 pt-[18vh]">
      <div className="w-full max-w-sm">
        <form
          onSubmit={onSubmit}
          className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            Password
            <input
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={cn(inputClass, "mt-1 font-normal")}
            />
          </label>

          {error && (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting || !password}
            className="mt-5 w-full"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign in
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-slate-400">
          Access is remembered on this device for 7 days.
        </p>
      </div>
    </div>
  );
}
